import React, { useRef, useState, useEffect } from 'react';
import { Button, DialogActions, IconButton, MenuItem, Radio, RadioGroup, Stack, Typography, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import * as yup from 'yup';
import { Formik, Form } from 'formik';
import FormTextField from '../../components/FormTextField';
import { useDispatch, useSelector } from 'react-redux';
import FileUploadField from '../../components/FileUploadField/FileUploadField';
import ProfileImageUploader from '../../components/ProfileImageUploader.js/ProfileImageUploader';
import { buttonStyle1, buttonStyle2, dialogButtonCancel } from '../../utils/commonStyle';
import AuthLayout from '../../components/AuthLayout';
import appapi from '../../api/apiClient';
import { initCompanyDetails, refreshUserData } from '../../redux/features/authSlice';
import { setLoading } from '../../redux/features/appSlice';
import { showmessage } from '../../utils/toastr';
import uploadFileToS3 from '../../utils/fileUpload';
import FormSelectField from '../../components/FormSelectField';
import CreateUnionRegistryAccount from './CreateUnionRegistryAccount';
import LanguageSwitcher from '../../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';

const validationSchema = yup.object({
  companyLogo: yup.string('Company Logo'),
  unionRegistryAccount: yup.string('Union Registry Account'),
  companyName: yup.string('Company Name').min(3, 'Company Name should be atleast 3 character').max(100, 'Maximum Character Limit 100').required(' '),
  regAddress: yup.string('Company Name').min(1, 'Company Name should be atleast 3 character').max(300, 'Maximum Character Limit 300').required(' '),
  countryName: yup.string('Country Name').required(' '),
  stateName: yup.string('state Name').required(' '),
  companyWebsite: yup
    .string()
    .nullable()
    .matches(/[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi, 'Enter valid url!'),
  zipCode: yup.string('Entity Type').required().min(1, 'Zip Code should be atleast 1 characters').max(8, 'Maximum Character Limit 8').required(' '),
  entityType: yup.string('Entity Type').required(),
  entityTypeSpecify: yup.string().min(3).max(100).label('Specific entity type').when(['entityType'], {
    is: (entityType) => {
      //any complex calulation
      return entityType === 'others';
    },
    then: yup.string().required(),
  }),
  euaBalanceAttachment: yup.string('EUA Balance Attachment'),
});

const entityTypes = [
  {
    entityId: 'private',
    entityName: 'Private',
  },
  {
    entityId: 'public',
    entityName: 'Public',
  },
  {
    entityId: 'others',
    entityName: 'Others',
  },
];

export default function OnBoardCompany({}) {
  const theme = useTheme();
  const formRef = useRef();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { userId, companyDetails, user } = useSelector((state) => state.auth);
  const { countryList } = useSelector((state) => state.layout);
  const [countryId, setCountryId] = useState(null);
  const [stateList, setStateList] = useState([]);
  const [showEUA, setShowEUA] = useState(false);

  useEffect(() => {
    if (companyDetails && companyDetails?.countryName) {
      const idObj = countryList.filter((res) => res.countryName === companyDetails?.countryName)[0];
      if (idObj) {
        setCountryId(idObj._id);
      }
    }
  }, [companyDetails?.countryName]);

  useEffect(() => {
    if (countryId) {
      getStateList();
    }
  }, [countryId]);

  const getStateList = async () => {
    const response = await appapi.get(`common/state/${countryId}`);
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        setStateList(data.response || []);
      } else {
        setStateList([]);
      }
    }
  };

  const handleFileLogo = (file) => {
    if (file?.path) {
      formRef?.current?.setFieldValue('companyLogo', file);
    } else {
      formRef?.current?.setFieldValue('companyLogo', undefined);
    }
  };

  const handleFile = (file) => {
    if (file?.path) {
      formRef?.current?.setFieldValue('euaBalanceAttachment', file);
    } else {
      formRef?.current?.setFieldValue('euaBalanceAttachment', undefined);
    }
  };

  const handleClose = (e) => {
    updateOnBoardStatus();
  };

  const updateOnBoardStatus = async () => {
    dispatch(setLoading(true));
    const response = await appapi.put(`/company-management/onBoardStatus/${companyDetails?._id}/true`, {});
    if (response.ok) {
      dispatch(setLoading(false));
      const data = response.data;
      if (data.status === '7400') {
        dispatch(refreshUserData({ ...user, detailsAdded: true }));
        // showmessage(data.message, 'success');
      } else {
        showmessage(data.message, 'error');
      }
    } else {
      dispatch(setLoading(false));
      showmessage(`Something Went Wrong!`, 'error', 2000);
    }
  };

  const handleUpdateCompany = async (values) => {
    dispatch(setLoading(true));
    const payload = values;
    if (values.entityType === 'others') {
      payload['entityType'] = values.entityTypeSpecify;
    }
    delete payload['entityTypeSpecify'];

    if (values.euaBalanceAttachment && values.euaBalanceAttachment?.path) {
      try {
        payload['euaBalanceAttachment'] = await uploadFileToS3(values.euaBalanceAttachment);
      } catch (e) {}
    }

    if (values.companyLogo && values.companyLogo?.path) {
      try {
        payload['companyLogo'] = await uploadFileToS3(values.companyLogo);
      } catch (e) {}
    }

    const response = await appapi.put(`/company-management/${companyDetails?._id}`, payload);
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        dispatch(initCompanyDetails());
        updateOnBoardStatus();
        // showmessage(data.message, 'success');
      } else {
        showmessage(data.message, 'error');
        dispatch(setLoading(false));
      }
    } else {
      dispatch(setLoading(false));
      showmessage(`Something Went Wrong!`, 'error', 2000);
    }
  };

  const entityType = companyDetails?.entityType ? (companyDetails?.entityType === 'private' || companyDetails?.entityType == 'public' ? companyDetails?.entityType : 'others') : undefined;

  return (
    <AuthLayout>
      <Stack flex={1}>
        {Boolean(companyDetails) ? (
          <Formik
            innerRef={formRef}
            enableReinitialize={false}
            initialValues={{
              companyLogo: companyDetails?.companyLogo ? companyDetails?.companyLogo : undefined,
              unionRegistryAccount: companyDetails?.unionRegistryAccount ? companyDetails?.unionRegistryAccount : undefined,
              companyName: companyDetails?.companyName ? companyDetails?.companyName : '',
              regAddress: companyDetails?.regAddress ? companyDetails?.regAddress : '',
              countryName: companyDetails?.countryName ? companyDetails?.countryName : undefined,
              stateName: companyDetails?.stateName ? companyDetails?.stateName : undefined,
              companyWebsite: companyDetails?.companyWebsite ? companyDetails?.companyWebsite : null,
              entityType: entityType,
              entityTypeSpecify: companyDetails?.entityType ? (companyDetails?.entityType === 'private' || companyDetails?.entityType === 'public' ? undefined : companyDetails?.entityType) : undefined,
              zipCode: companyDetails?.zipCode ? companyDetails?.zipCode : undefined,
              euaBalance: companyDetails?.euaBalance ? companyDetails?.euaBalance : undefined,
              euaBalanceAttachment: companyDetails?.euaBalanceAttachment ? companyDetails?.euaBalanceAttachment : undefined,
            }}
            validationSchema={validationSchema}
            onSubmit={(values) => {
              handleUpdateCompany(values);
            }}
          >
            {({ name, values, touched, errors, handleChange, handleBlur, isValid, dirty, setFieldValue }) => (
              <Form
                autoComplete="off"
                className="flex flex-col flex-1 items-center"
              >
                <Stack
                  flex={1}
                  className="bg-white min-w-[50vw] max-w-[50vw] my-[48px] rounded-[8px]"
                >
                  <Stack
                    sx={{ borderBottom: '1px solid #14285A1A' }}
                    alignItems="center"
                    direction="row"
                    className="h-[56px] px-[16px]"
                  >
                    <Typography
                      color={theme.palette.primary.text}
                      className="fw-medium fs-14 ff-Roboto uppercase"
                    >
                      {t('Company details')}
                    </Typography>
                  </Stack>

                  <Stack direction="row">
                    <Stack flex={4}>
                      <Stack className="pl-[32px] pt-[24px]">
                        <Typography
                          className="fw-medium fs-12 ff-Roboto"
                          color={theme.palette.primary.text}
                        >
                          {t('Company logo')}
                        </Typography>
                        <Typography
                          className="fw-medium fs-10 ff-Roboto !mb-[10px]"
                          color={theme.palette.primary.text}
                        >
                          {t('File size should be less than 1MB')}
                        </Typography>
                        <Stack className="w-[120px] h-[120px]">
                          <ProfileImageUploader
                            url={values?.companyLogo ? values?.companyLogo : null}
                            inputId={`doc`}
                            handleFile={(files) => handleFileLogo(files)}
                            showFileName={false}
                          />
                        </Stack>
                      </Stack>
                    </Stack>
                    <Stack flex={8}>
                      <Stack
                        className="overflow-auto pt-[16px]"
                        sx={{ maxHeight: 'calc(100vh - 210px)' }}
                      >
                        <Typography
                          className="fw-medium fs-20 ff-Roboto"
                          color={theme.palette.primary.text}
                        >
                          {companyDetails?.companyName}
                        </Typography>
                        <Typography
                          className="fw-regular fs-16 ff-Roboto capitalize"
                          color={theme.palette.primary.text}
                        >
                          {companyDetails?.companyType}
                        </Typography>

                        <Stack
                          style={{ backgroundColor: 'white', borderRadius: '8px' }}
                          className="mt-[32px] pr-[16px]"
                        >
                          <Stack>
                            <FormTextField
                              outlineLabel={t('Union Registry Account')}
                              name="unionRegistryAccount"
                              whiteSpace={true}
                              inputProps={{ maxLength: 101 }}
                            />
                            <Stack
                              direction={'row'}
                              alignItems={'center'}
                              style={{ marginBottom: '20px' }}
                              alignSelf={'flex-end'}
                            >
                              <Typography
                                className="fw-medium fs-12 ff-Roboto"
                                color={theme.palette.primary.text}
                              >
                                {t('Dont have an Union Registry Account?')}
                              </Typography>
                              <Typography
                                onClick={() => setShowEUA(true)}
                                className="fw-medium fs-12 ff-Roboto !ml-[5px] cursor-pointer"
                                color={'#1754FF'}
                              >
                                {t('Click here')}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack>
                            <FormTextField
                              outlineLabel={t('Legal entity type')}
                              fullWidth
                              id={'entityType'}
                              name="entityType"
                              requiredMark={true}
                              select
                              sx={{ marginBottom: '25px !important' }}
                              onSelect={(e) => {
                                if (e !== 'others') {
                                  setFieldValue('entityTypeSpecify', '');
                                }
                              }}
                            >
                              {entityTypes?.map((item) => (
                                <MenuItem
                                  key={item.entityId}
                                  value={item.entityId}
                                >
                                  {item.entityName}
                                </MenuItem>
                              ))}
                            </FormTextField>
                          </Stack>
                          {values.entityType === 'others' && (
                            <Stack>
                              <FormTextField
                                outlineLabel={t('Specify Entity Type')}
                                name="entityTypeSpecify"
                                requiredMark={true}
                                onlyAlpha={true}
                                whiteSpace={true}
                                inputProps={{ maxLength: 101 }}
                              />
                            </Stack>
                          )}
                          <Stack>
                            <FormTextField
                              outlineLabel={t('Website')}
                              name="companyWebsite"
                              requiredMark={false}
                              lowerCase={true}
                              inputProps={{ maxLength: 200 }}
                            />
                          </Stack>
                          <Stack>
                            <FormTextField
                              outlineLabel={t('Address')}
                              name="regAddress"
                              requiredMark={true}
                              whiteSpace={true}
                              inputProps={{ maxLength: 301 }}
                            />
                          </Stack>
                          <Stack>
                            <FormSelectField
                              name="countryName"
                              label={`${t('Country')} *`}
                              onSelect={(e) => {
                                setFieldValue('stateName', undefined);
                                const idObj = countryList.filter((res) => res.countryName === e)[0];
                                if (idObj) {
                                  setCountryId(idObj._id);
                                }
                              }}
                            >
                              {countryList?.map((item) => (
                                <MenuItem
                                  key={item.countryName}
                                  value={item.countryName}
                                >
                                  <div style={{ textTransform: 'capitalize' }}>{item.countryName}</div>
                                </MenuItem>
                              ))}
                            </FormSelectField>
                          </Stack>
                          <Stack>
                            <FormSelectField
                              name="stateName"
                              label={`${t('State/Province')} *`}
                            >
                              {stateList?.length === 0 && (
                                <MenuItem
                                  disabled
                                  value=""
                                >
                                  <em>No Data Found</em>
                                </MenuItem>
                              )}
                              {stateList?.map((item) => (
                                <MenuItem
                                  key={item.stateName}
                                  value={item.stateName}
                                >
                                  <div style={{ textTransform: 'capitalize' }}>{item.stateName}</div>
                                </MenuItem>
                              ))}
                            </FormSelectField>
                          </Stack>

                          <Stack>
                            <FormTextField
                              outlineLabel={t('Zip Code')}
                              fullWidth
                              id={'zipCode'}
                              name="zipCode"
                              requiredMark={true}
                              inputProps={{ maxLength: 8 }}
                              sx={{ marginBottom: '25px !important' }}
                            ></FormTextField>
                          </Stack>
                          <Stack>
                            <FormTextField
                              outlineLabel={t('Actual EUA Balance')}
                              fullWidth
                              id={'eua'}
                              name="euaBalance"
                              onlyNumber={true}
                              requiredMark={false}
                              sx={{ marginBottom: '25px !important' }}
                            ></FormTextField>
                          </Stack>
                          <Stack style={{ paddingBottom: '20px' }}>
                            <FileUploadField
                              placeholder={t('Upload EUA evidence (Max 5MB)')}
                              url={values?.euaBalanceAttachment ? values?.euaBalanceAttachment : null}
                              inputId={`doc`}
                              handleFile={(files) => handleFile(files)}
                              showFileName={false}
                            />
                          </Stack>
                        </Stack>
                      </Stack>
                      <Stack
                        direction="row"
                        justifyContent="flex-end"
                        className="h-[62px] py-[16px] pr-[16px] bg-white"
                      >
                        <Button
                          type="button"
                          sx={[
                            dialogButtonCancel,
                            {
                              color: theme.palette.primary.main,
                              '&:hover': {},
                            },
                          ]}
                          onClick={handleClose}
                        >
                          {t('Skip')}
                        </Button>
                        <Button
                          sx={[
                            buttonStyle2,
                            {
                              color: '#fff',
                              backgroundColor: theme.palette.primary.main,
                              '&:hover': {
                                backgroundColor: theme.palette.background.main,
                                color: '#fff',
                              },
                              '&:disabled': {
                                color: 'rgba(255,255,255,0.5)',
                                backgroundColor: theme.palette.primary.main,
                              },
                            },
                          ]}
                          disabled={!(dirty && isValid)}
                          type="submit"
                          variant="contained"
                        >
                          {t('Next')}
                        </Button>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </Form>
            )}
          </Formik>
        ) : (
          <Stack></Stack>
        )}
      </Stack>
      {showEUA && (
        <CreateUnionRegistryAccount
          modalOpen={showEUA}
          onModelHandleClose={() => setShowEUA(false)}
        />
      )}
       <div style={{ position:'absolute', top: '16px', right: '16px'}}>
        <LanguageSwitcher />
      </div>
    </AuthLayout>
  );
}
